<template>
  <div class="cms-guides">
    <cms-page-header
      :title="$t('cms.guides.title')"
      :create-text="$t('cms.guides.createNew')"
      @create="navigateToCreate"
    />

    <cms-filter-bar>
      <cms-filter-group :label="$t('cms.guides.filters.language')">
        <select v-model="selectedLanguage" class="cms-select">
          <option v-for="language in languageOptions" :key="language.value" :value="language.value">
            {{ language.label }}
          </option>
        </select>
      </cms-filter-group>

      <cms-filter-group :label="$t('general.search')">
        <input
          v-model="searchQuery"
          type="text"
          class="cms-input"
          :placeholder="$t('cms.guides.searchPlaceholder')"
        />
      </cms-filter-group>

      <cms-filter-group :label="''">
        <button class="cms-btn cms-btn-secondary" type="button" :disabled="isLoading" @click="loadGuides">
          <v-icon icon="refresh" size="sm" />
          <span>{{ $t('general.refresh') }}</span>
      </button>
      </cms-filter-group>
    </cms-filter-bar>

    <div v-if="errorMessage" class="cms-guides-error">
      <p>{{ errorMessage }}</p>
      <button type="button" class="cms-btn-link" @click="loadGuides">{{ $t('general.retry') }}</button>
    </div>

    <div v-if="isLoading" class="cms-guides-loading">
      <div class="cms-loading-spinner" />
      <p>{{ $t('general.loading') }}</p>
    </div>

    <cms-empty-state
      v-else-if="filteredGuides.length === 0"
      :message="$t('cms.guides.list.emptyStateTitle')"
      :hint="$t('cms.guides.list.emptyStateHint')"
    />

    <cms-grid v-else min-width="360px">
      <cms-card
        v-for="guide in filteredGuides"
        :key="guide.id"
        clickable
        class="cms-guide-card"
        @click="navigateToEdit(guide.id)"
      >
        <template #status>
          <cms-status-badge :status="guide.status">
            {{ guide.status }}
          </cms-status-badge>
        </template>

        <template #actions>
          <cms-icon-button
            :icon="guide.isPublic ? 'eye-blocked' : 'eye'"
            :variant="guide.isPublic ? 'secondary' : 'primary'"
            :disabled="publishingId === guide.id"
            :tooltip="guide.isPublic ? $t('cms.guides.unpublishSuccess') : $t('cms.guides.publishSuccess')"
            @click.stop="togglePublish(guide)"
          />
          <cms-icon-button
            icon="pencil"
            variant="primary"
            :tooltip="$t('general.edit')"
            @click.stop="navigateToEdit(guide.id)"
          />
          <cms-icon-button
            icon="trash"
            variant="danger"
            :disabled="deletingId === guide.id"
            :tooltip="$t('general.delete')"
            @click.stop="deleteGuide(guide)"
          />
        </template>

        <div v-if="guide.featuredImage" class="cms-guide-preview" :style="{ backgroundImage: `url(${guide.featuredImage})` }" />
        <div v-else class="cms-guide-preview-placeholder">
          <v-icon icon="user" size="lg" />
        </div>

        <div class="cms-guide-header">
          <h3 class="cms-guide-title">{{ guide.title }}</h3>
          <span v-if="guide.isFeatured" class="cms-guide-featured">{{ $t('cms.activities.featuredBadge') }}</span>
        </div>

        <p v-if="guide.tagline" class="cms-guide-tagline">
          {{ guide.tagline }}
        </p>

        <ul class="cms-guide-meta-minimal">
          <li v-if="guide.userName && guide.userName !== '-'">
            <v-icon icon="user" size="sm" />
            <span>{{ guide.userName }}</span>
          </li>
          <li v-if="guide.relatedActivitiesCount > 0">
            <v-icon icon="map" size="sm" />
            <span>{{ guide.relatedActivitiesCount }} {{ $t('cms.guides.relatedActivities') }}</span>
          </li>
        </ul>

        <template #footer>
          <div class="cms-card-footer">
            {{ console.log(guide) }}
            <span class="cms-guide-date">{{ guide.updatedAtLabel }}</span>
          </div>
        </template>
      </cms-card>
    </cms-grid>

    <cms-pagination
      v-if="paginationMeta"
      :meta="paginationMeta"
      :per-page-options="[9, 18, 27, 36]"
      @page-changed="handlePageChange"
      @per-page-changed="handlePerPageChange"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useToast } from 'vue-toastification';
import { useI18n } from 'vue-i18n';
import VIcon from '@/components/vendor/basic/icon/VIcon.vue';
import {
  CmsPageHeader,
  CmsFilterBar,
  CmsFilterGroup,
  CmsGrid,
  CmsCard,
  CmsStatusBadge,
  CmsIconButton,
  CmsEmptyState,
  CmsPagination,
} from '@/components/cms';
import api from '@/api';
import { useAuthStore } from '@/stores/auth';
import { useAppLanguages } from '@/composables/useAppLanguages';
import { normalizeResource, extractRelationship } from '@/composables/useCmsActivityData';

interface CmsGuideCard {
  id: number;
  title: string;
  tagline: string;
  bio: string;
  bioPreview: string;
  userName: string;
  memberSince: string | null;
  memberSinceLabel: string;
  relatedActivitiesCount: number;
  updatedAt: string | null;
  updatedAtLabel: string;
  locale: string;
  featuredImage?: string;
  isFeatured: boolean;
  isPublic: boolean;
  status: 'draft' | 'published';
}

export default defineComponent({
  name: 'TheCmsGuides',
  components: {
    CmsPageHeader,
    CmsFilterBar,
    CmsFilterGroup,
    CmsGrid,
    CmsCard,
    CmsStatusBadge,
    CmsIconButton,
    CmsEmptyState,
    CmsPagination,
    VIcon,
  },
  setup() {
    const router = useRouter();
    const toast = useToast();
    const { t } = useI18n();
    const authStore = useAuthStore();

    const agencySlug = computed(() => authStore.agency?.slug ?? null);
    const { locales, resolveStoredLocale } = useAppLanguages();
    const currentLocale = ref(resolveStoredLocale(localStorage.getItem('language')));

    const guides = ref<CmsGuideCard[]>([]);
    const isLoading = ref(false);
    const errorMessage = ref('');
    const deletingId = ref<number | null>(null);
    const publishingId = ref<number | null>(null);

    const selectedLanguage = ref(currentLocale.value);
    const searchQuery = ref('');

    // Pagination state
    const currentPage = ref(1);
    const perPage = ref(9);
    const paginationMeta = ref<{
      currentPage: number;
      lastPage: number;
      perPage: number;
      from: number | null;
      to: number | null;
      total: number;
    } | null>(null);

    const languageOptions = computed(() =>
      locales.value.map((locale) => ({
        value: locale,
        label: locale.toUpperCase(),
      })),
    );

    // Since filtering is now done server-side, filteredGuides just returns guides
    const filteredGuides = computed(() => guides.value);

    const extractListData = (response: any): any[] => {
      if (!response) return [];
      const candidates = [
        response?.data?.data,
        response?.data,
        response?.data?.data?.data,
        response,
      ];
      for (const candidate of candidates) {
        if (Array.isArray(candidate)) {
          return candidate;
        }
      }
      return [];
    };

    const formatDate = (value: string | null | undefined) => {
      if (!value) return '';
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) {
        return '';
      }
      return date.toLocaleDateString();
    };

    const formatDateTime = (value: string | null | undefined) => {
      if (!value) return '';
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) {
        return '';
      }
      return date.toLocaleString();
    };

    const stripHtml = (html: string, maxLength = 150) => {
      if (!html) return '';
      const text = html.replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim();
      return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
    };

    const extractMediaItems = (guide: any) => {
      
      const relationship = extractRelationship(guide, 'media');
      const normalized = normalizeResource(relationship);
      if (Array.isArray(normalized)) {
        return normalized;
      }
      if (Array.isArray(guide?.media)) {
        return guide.media;
      }
      return [];
    };

    const getMediaUrl = (item: any) => {
      if (!item) return '';
      const attributes = item?.attributes || {};
      return (
        item.original_url ||
        item.url ||
        attributes.original_url ||
        attributes.url ||
        attributes.preview_url ||
        ''
      );
    };

    const extractTranslations = (guide: any) => {
      const relationship = extractRelationship(guide, 'translations');
      const normalizedRelationship = normalizeResource(relationship);
      if (Array.isArray(normalizedRelationship)) {
        return normalizedRelationship;
      }
      if (Array.isArray(guide?.translations)) {
        return guide.translations;
      }
      if (normalizedRelationship) {
        return [normalizedRelationship];
      }
      return [];
    };

    const mapGuideRecord = (item: any): CmsGuideCard => {
      const normalized = normalizeResource(item) || item;
      const translations = extractTranslations(normalized);
      const translation =
        translations.find((tr: any) => (tr?.locale || '').toLowerCase() === selectedLanguage.value) ||
        translations[0] ||
        {};

      const relatedActivities = extractRelationship(normalized, 'relatedActivities');
      const normalizedActivities = normalizeResource(relatedActivities);
      const activitiesArray = Array.isArray(normalizedActivities)
        ? normalizedActivities
        : normalizedActivities
          ? [normalizedActivities]
          : [];

      // Extract user name from agency_user relationship
      const agencyUserRelation = normalizeResource(extractRelationship(normalized, 'agencyUser')) || normalized.agencyUser || normalized.agency_user || {};
      const userName =
        agencyUserRelation?.user?.name ||
        agencyUserRelation?.name ||
        normalized?.agencyUser?.user?.name ||
        normalized?.agency_user?.user?.name ||
        '-';

      const mediaItems = extractMediaItems(normalized);
      const featuredMedia = mediaItems[0]?.originalUrl || '';

      const bio = translation?.bio || normalized.bio || '';
      const bioPreview = stripHtml(bio);
      console.log(normalized);
      
      return {
        id: normalized.id,
        title: translation?.title || normalized.title || '-',
        tagline: translation?.tagline || normalized.tagline || '',
        bio,
        bioPreview,
        userName,
        memberSince: normalized.memberSince || null,
        memberSinceLabel: formatDate(normalized.memberSince),
        relatedActivitiesCount: activitiesArray.length,
        updatedAt: normalized.updatedAt || null,
        updatedAtLabel: formatDateTime(normalized.updatedAt),
        locale: (translation?.locale || selectedLanguage.value || 'en').toLowerCase(),
        featuredImage: featuredMedia,
        isFeatured: !!normalized.isFeatured,
        isPublic: !!normalized.isPublic,
        status: normalized.isPublic ? 'published' : 'draft',
      };
    };

    const buildFilters = () => {
      const filters: Record<string, any> = {
        language: selectedLanguage.value || currentLocale.value,
      };

      if (searchQuery.value.trim()) {
        filters.search = searchQuery.value.trim();
      }

      return filters;
    };

    const loadGuides = async () => {
      if (!agencySlug.value) {
        return;
      }

      isLoading.value = true;
      errorMessage.value = '';

      try {
        const response = await api.guiderService.listCmsGuiders(agencySlug.value, {
          page: currentPage.value,
          perPage: perPage.value,
          filters: buildFilters(),
          order: { key: 'updated_at', direction: 'desc' },
        });
        const list = extractListData(response);
        guides.value = list.map(mapGuideRecord);
        
        // Extract pagination meta from response
        if (response?.meta) {
          paginationMeta.value = {
            currentPage: response.meta.current_page || currentPage.value,
            lastPage: response.meta.last_page || 1,
            perPage: response.meta.per_page || perPage.value,
            from: response.meta.from || null,
            to: response.meta.to || null,
            total: response.meta.total || 0,
          };
        } else {
          paginationMeta.value = null;
        }
      } catch (error: any) {
        console.error('Failed to load CMS guides', error);
        errorMessage.value = error?.response?.data?.message || t('general.errorMessage');
        toast.error(errorMessage.value);
      } finally {
        isLoading.value = false;
      }
    };

    const handlePageChange = (page: number) => {
      currentPage.value = page;
      loadGuides();
    };

    const handlePerPageChange = (newPerPage: number) => {
      perPage.value = newPerPage;
      currentPage.value = 1; // Reset to first page when changing per page
      loadGuides();
    };

    watch(
      () => agencySlug.value,
      (slug) => {
        if (slug) {
          currentPage.value = 1;
          loadGuides();
        }
      },
      { immediate: true },
    );

    watch(
      () => selectedLanguage,
      () => {
        if (!agencySlug.value) return;
        currentPage.value = 1;
        loadGuides();
      },
    );

    let searchDebounce: ReturnType<typeof setTimeout> | null = null;
    watch(
      () => searchQuery.value,
      () => {
        if (!agencySlug.value) return;
        if (searchDebounce) {
          clearTimeout(searchDebounce);
        }
        searchDebounce = setTimeout(() => {
          currentPage.value = 1;
          loadGuides();
        }, 400);
      },
    );

    const navigateToCreate = () => {
      router.push({ name: 'cms.guiders.create' });
    };

    const navigateToEdit = (guideId: number) => {
      router.push({ name: 'cms.guiders.edit', params: { id: guideId } });
    };

    const togglePublish = async (guide: CmsGuideCard) => {
      if (!agencySlug.value) return;
      
      publishingId.value = guide.id;
      try {
        await api.guiderService.publishCmsGuider(agencySlug.value, guide.id, {
          is_public: !guide.isPublic,
        });
        
        // Update the guide in the list
        const index = guides.value.findIndex((g) => g.id === guide.id);
        if (index !== -1) {
          guides.value[index].isPublic = !guide.isPublic;
        }
        
        toast.success(
          guide.isPublic 
            ? t('cms.guides.unpublishSuccess') 
            : t('cms.guides.publishSuccess')
        );
      } catch (error: any) {
        console.error('Failed to toggle publish status', error);
        const message = error?.response?.data?.message || t('general.errorMessage');
        toast.error(message);
      } finally {
        publishingId.value = null;
      }
    };

    const deleteGuide = async (guide: CmsGuideCard) => {
      if (!agencySlug.value) return;
      if (!window.confirm(t('general.deleteConfirmation') || 'Are you sure?')) {
        return;
      }

      deletingId.value = guide.id;
      try {
        await api.guiderService.deleteCmsGuider(agencySlug.value, guide.id);
        guides.value = guides.value.filter((item) => item.id !== guide.id);
        toast.success(t('cms.guides.deleteSuccess'));
      } catch (error: any) {
        console.error('Failed to delete guide', error);
        const message = error?.response?.data?.message || t('general.errorMessage');
        toast.error(message);
      } finally {
        deletingId.value = null;
      }
    };

    return {
      guides,
      filteredGuides,
      selectedLanguage,
      searchQuery,
      languageOptions,
      isLoading,
      errorMessage,
      deletingId,
      publishingId,
      loadGuides,
      navigateToCreate,
      navigateToEdit,
      togglePublish,
      deleteGuide,
      paginationMeta,
      handlePageChange,
      handlePerPageChange,
    };
  },
});
</script>

<style lang="scss" scoped>
@import '@/components/cms/cms-styles.scss';

// ============================================
// VIEW-SPECIFIC STYLES
// ============================================
// Only styles unique to this view

.cms-guides {
  color: $cms-text-primary;
}

// Loading state (unique to this view)
.cms-guides-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: $cms-spacing-xxl 0;
  gap: $cms-spacing-sm;
  color: $cms-text-secondary;
}

.cms-loading-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid rgba(0, 0, 0, 0.1);
  border-top-color: $cms-primary-color;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

// Error state (unique to this view)
.cms-guides-error {
  display: flex;
  align-items: center;
  gap: $cms-spacing-md;
  padding: $cms-spacing-md;
  margin-bottom: $cms-spacing-md;
  background: $cms-danger-light;
  border: 1px solid $cms-danger-border;
  border-radius: $cms-radius-md;
  color: $cms-danger-color;
}

.cms-btn-link {
  background: none;
  border: none;
  padding: 0;
  color: $cms-primary-color;
  cursor: pointer;
  font-weight: 500;
  text-decoration: underline;
}

// Guide card specific styles (unique to this view)
.cms-guide-card {
  display: flex;
  flex-direction: column;
  overflow: hidden;
  transition: all $cms-transition-base;
  padding: 0 !important;
  
  :deep(.cms-card-header) {
    padding: $cms-spacing-sm $cms-spacing-md 0;
    margin-bottom: $cms-spacing-sm;
  }

  :deep(.cms-card-header-right) {
    gap: $cms-spacing-xs;
  }

  :deep(.cms-card-content) {
    padding: 0 !important;
    gap: 0;
  }
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.12);
    
    .cms-guide-preview {
      transform: scale(1.05);
    }
    
    .cms-guide-title {
      color: $cms-primary-color;
    }
  }
}

.cms-guide-preview {
  width: 100%;
  height: 190px;
  background-size: cover;
  background-position: center;
  position: relative;
  overflow: hidden;
  transition: transform 0.25s ease;
  background-color: $cms-background-gray;
  
  &::before {
    display: none;
  }
  
  &::after {
    display: none;
  }
}

.cms-guide-preview-placeholder {
  width: 100%;
  height: 190px;
  background: linear-gradient(135deg, rgba($cms-primary-color, 0.08) 0%, rgba($cms-primary-color, 0.03) 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba($cms-primary-color, 0.35);
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background-image: 
      repeating-linear-gradient(45deg, transparent, transparent 12px, rgba($cms-primary-color, 0.04) 12px, rgba($cms-primary-color, 0.04) 24px);
    pointer-events: none;
  }
  
  :deep(svg) {
    position: relative;
    z-index: 1;
    filter: drop-shadow(0 2px 6px rgba(0, 0, 0, 0.15));
    opacity: 0.6;
  }
}

.cms-guide-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: $cms-spacing-sm;
  padding: $cms-spacing-sm $cms-spacing-md 0 $cms-spacing-md;
  min-height: 2.5rem;
}

.cms-guide-title {
  font-size: $cms-font-size-lg;
  font-weight: 700;
  margin: 0;
  color: $cms-text-primary;
  flex: 1;
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  transition: color $cms-transition-base;
  letter-spacing: -0.01em;
}

.cms-guide-featured {
  display: inline-flex;
  align-items: center;
  padding: 0.35rem 0.65rem;
  background: linear-gradient(135deg, $cms-primary-color 0%, $cms-primary-dark 100%);
  color: white;
  border-radius: $cms-radius-sm;
  font-size: $cms-font-size-xs;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  flex-shrink: 0;
  box-shadow: 0 2px 8px rgba($cms-primary-color, 0.3);
  margin-top: 2px;
}

.cms-guide-tagline {
  margin: 0;
  padding: 0 $cms-spacing-md $cms-spacing-xs;
  font-size: $cms-font-size-sm;
  color: $cms-text-secondary;
  line-height: 1.45;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.cms-guide-meta-minimal {
  list-style: none;
  margin: 0;
  padding: 0 $cms-spacing-md $cms-spacing-sm;
  display: flex;
  flex-wrap: wrap;
  gap: $cms-spacing-sm;

  li {
    display: inline-flex;
    align-items: center;
    gap: $cms-spacing-xs;
    font-size: $cms-font-size-xs;
    color: $cms-text-secondary;
    padding: 2px 8px;
    border-radius: $cms-radius-sm;
    background: rgba(0, 0, 0, 0.04);
  }
}

.cms-card-footer {
  display: flex;
  justify-content: flex-end;
  font-size: $cms-font-size-xs;
  color: $cms-text-muted;
  padding: $cms-spacing-sm $cms-spacing-md;
  margin-top: 0;
  background: transparent;
  border-top: 1px solid $cms-border-color-light;
}

.cms-guide-date {
  color: $cms-text-muted;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style> 
